import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useClientAuth } from '../../auth/ClientAuthContext';
import API_BASE from '../../utils/api';
import { documentLabel } from '../../utils/documentTitle';
import PublicStripePaymentModal from '../../components/Projects/Invoices/PublicStripePaymentModal';
import { depositRequestLabel, depositStillDue } from '../../utils/invoiceAdjustments';

export default function ClientInvoiceDetail() {
  const { projectId, invoiceId } = useParams();
  const { clientAuth } = useClientAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesNote, setChangesNote] = useState('');
  const [submittingChanges, setSubmittingChanges] = useState(false);
  const [changesError, setChangesError] = useState('');
  const [changesSent, setChangesSent] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (!clientAuth?.token) return;

    fetch(`${API_BASE}/api/client/projects/${projectId}/invoices/${invoiceId}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${clientAuth.token}` },
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
        return data;
      })
      .then(setInvoice)
      .catch((err) => setError(err.message || 'Failed to load invoice.'))
      .finally(() => setLoading(false));
  }, [clientAuth, projectId, invoiceId]);

  async function handleRequestChanges() {
    setChangesError('');
    setSubmittingChanges(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/client/projects/${projectId}/invoices/${invoiceId}/request-changes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${clientAuth.token}` },
          credentials: 'include',
          body: JSON.stringify({ note: changesNote }),
        }
      );
      const data = await res.json();
      if (!res.ok) { setChangesError(data?.error || 'Failed to submit. Please try again.'); return; }
      setChangesSent(true);
      setShowChangesModal(false);
      setChangesNote('');
    } catch {
      setChangesError('Something went wrong. Please try again.');
    } finally {
      setSubmittingChanges(false);
    }
  }

  if (loading) return <p style={{ color: '#6b7280', fontSize: 14 }}>Loading…</p>;
  if (error) return <><Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link><p className="cp-error">{error}</p></>;
  if (!invoice) return null;

  const lineItems = Array.isArray(invoice.lineItems) ? invoice.lineItems : [];
  const totals = invoice.totals || {};
  const remaining =
    invoice.remainingBalance ??
    Math.max(Number(((totals.total || 0) - (totals.paid || 0)).toFixed(2)), 0);
  const isFullyPaid = remaining <= 0;
  const depositRequested = Number(totals.depositRequested || 0);
  const depositDue = isFullyPaid ? 0 : depositStillDue(depositRequested, totals.paid);

  return (
    <div className="cp-animate">
      <Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link>

      <div className="cp-card">
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              {documentLabel(invoice.invoiceNumber, invoice.title) || `Invoice`}
            </h1>
            {invoice.invoiceDate && (
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Date: {new Date(invoice.invoiceDate).toLocaleDateString()}
              </p>
            )}
          </div>
          <InvoiceStatusBadge status={invoice.status} />
        </div>

        {/* Line items */}
        {lineItems.length > 0 && (
          <table className="cp-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="center">Qty</th>
                <th className="right">Unit Price</th>
                <th className="right">Total</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <tr key={item?.itemId || i}>
                  <td>{item?.clientName || item?.name || item?.description || '—'}</td>
                  <td className="center">{item?.qty ?? '—'}</td>
                  <td className="right">${Number(item?.clientUnit || 0).toFixed(2)}</td>
                  <td className="right">${Number(item?.lineTotal || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Totals */}
        <div className="cp-totals">
          <div className="cp-totals-inner">
            <TotalRow label="Subtotal" value={totals.subtotal} />
            {totals.tax > 0 && <TotalRow label="Sales Tax" value={totals.tax} />}
            {totals.procurementFee > 0 && <TotalRow label="Procurement Fee" value={totals.procurementFee} />}
            <TotalRow label="Total" value={totals.total} bold />
            {totals.paid > 0 && <TotalRow label="Paid" value={totals.paid} />}
            {remaining > 0 && <TotalRow label="Balance Due" value={remaining} highlight />}
            {depositRequested > 0 && (
              <TotalRow label={depositRequestLabel(invoice.depositRequest)} value={depositRequested} />
            )}
            {depositDue > 0 && <TotalRow label="Deposit Due Now" value={depositDue} bold />}
          </div>
        </div>

        {/* Attachments */}
        {Array.isArray(invoice.attachments) && invoice.attachments.length > 0 && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 10px' }}>Attachments</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {invoice.attachments.map((a) => (
                <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                  {a.name || 'Download attachment'}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Action bar */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 8, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {changesSent ? (
            <p style={{ fontSize: 13, color: '#065f46', margin: 0 }}>Change request sent — your designer has been notified.</p>
          ) : (
            <button className="cp-btn-ghost" onClick={() => setShowChangesModal(true)}>
              <MessageSquare size={15} />
              Request Changes
            </button>
          )}
          {!isFullyPaid && (
            <button className="cp-btn-primary" onClick={() => setShowPayment(true)}>
              Pay Now
            </button>
          )}
        </div>

        {/* Payment history */}
        {Array.isArray(invoice.paymentHistory) && invoice.paymentHistory.length > 0 && (
          <div className="cp-payment-history">
            <h3>Payment History</h3>
            {invoice.paymentHistory.map((p, i) => (
              <div key={i} className="cp-payment-row">
                <span>
                  {p.date ? new Date(p.date).toLocaleDateString() : '—'}
                  {(p.method || p.paymentType) ? ` · ${p.method || p.paymentType}` : ''}
                </span>
                <span style={{ fontWeight: 600 }}>${Number(p.amount || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stripe payment modal */}
      {showPayment && (
        <PublicStripePaymentModal
          remainingBalance={remaining}
          defaultAmount={depositDue}
          invoiceNumber={invoice.invoiceNumber}
          createIntentEndpoint={`${API_BASE}/api/client/projects/${projectId}/invoices/${invoiceId}/create-payment-intent`}
          applyPaymentEndpoint={`${API_BASE}/api/client/projects/${projectId}/invoices/${invoiceId}/apply-payment`}
          headers={{ Authorization: `Bearer ${clientAuth.token}` }}
          metadata={{ invoiceId, projectId }}
          onSuccess={(amount) => {
            setInvoice((prev) => {
              const newPaid = Number(((prev.totals?.paid || 0) + amount).toFixed(2));
              const newRemaining = Math.max(
                Number(((prev.totals?.total || 0) - newPaid).toFixed(2)),
                0
              );
              return {
                ...prev,
                totals: { ...prev.totals, paid: newPaid },
                remainingBalance: newRemaining,
                status: newRemaining <= 0 ? 'paid' : newPaid > 0 ? 'partial' : prev.status,
              };
            });
            setShowPayment(false);
          }}
          onClose={() => setShowPayment(false)}
        />
      )}

      {/* Request Changes modal */}
      {showChangesModal && (
        <div className="cp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowChangesModal(false)}>
          <div className="cp-modal">
            <h2>Request Changes</h2>
            <p>Describe the issue or what you'd like corrected. Your designer will be notified by email.</p>
            <label className="cp-label">Your message</label>
            <textarea
              className="cp-input"
              rows={4}
              value={changesNote}
              onChange={(e) => setChangesNote(e.target.value)}
              placeholder="e.g. Line item 3 has an incorrect quantity…"
              autoFocus
              style={{ resize: 'vertical' }}
            />
            {changesError && <p className="cp-error" style={{ marginTop: 12 }}>{changesError}</p>}
            <div className="cp-modal-actions">
              <button className="cp-btn-ghost" onClick={() => setShowChangesModal(false)} disabled={submittingChanges}>Cancel</button>
              <button className="cp-btn-primary" onClick={handleRequestChanges} disabled={submittingChanges || !changesNote.trim()}>
                {submittingChanges ? 'Sending…' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceStatusBadge({ status }) {
  const map = {
    paid:    { cls: 'cp-badge-green',  label: 'Paid' },
    partial: { cls: 'cp-badge-yellow', label: 'Partial' },
    unpaid:  { cls: 'cp-badge-red',    label: 'Unpaid' },
    draft:   { cls: 'cp-badge-gray',   label: 'Draft' },
  };
  const s = map[String(status).toLowerCase()] || map.draft;
  return <span className={`cp-badge ${s.cls}`}>{s.label}</span>;
}

function TotalRow({ label, value, bold, highlight }) {
  return (
    <div className={`cp-total-row${bold ? ' bold' : ''}${highlight ? ' highlight' : ''}`}>
      <span>{label}</span>
      <span>${Number(value || 0).toFixed(2)}</span>
    </div>
  );
}
